import React from 'react';
import { useBilling } from 'src/hooks/useBilling';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { StripeBanksElementForm } from '@features/onboarding/components/7-finish/components/stripe-payment/StripeBanksElementForm';
import { usePaymentStore } from '@features/onboarding/components/7-finish/store/payment';

export const BillingAccounts = () => {
  const { paymentMethods, subscriptionPlans, isLoading, refetch } = useBilling() as any;
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const { paymentMethodId } = usePaymentStore();

  React.useEffect(() => {
    if (paymentMethodId) {
      setIsEditOpen(false);
      // Обновим список платёжных методов после сохранения
      if (typeof refetch === 'function') {
        refetch();
      }
    }
  }, [paymentMethodId, refetch]);

  if (isLoading) {
    return (
      <section className="flex flex-col rounded-3xl pb-18 bg-profile-section-bg">
        <div className="bg-profile-header-bg text-white px-15 max-md:px-10 max-xs:px-5 rounded-t-3xl py-3.5 pb-2 text-[42px] tracking-wide max-md:text-3xl max-md:py-5">
          Billing Accounts
        </div>
        <div className="flex justify-center items-center py-8">
          <div><AnimatedDots /></div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col rounded-3xl pb-18 bg-profile-section-bg">
      <div className="bg-profile-header-bg text-white px-15 max-md:px-10 max-xs:px-5 rounded-t-3xl py-3.5 pb-2 text-[42px] tracking-wide max-md:text-3xl max-md:py-5">
        Billing Accounts
      </div>
      <div className="flex flex-col px-14 mt-10 bg-profile-section-bg max-md:px-10 max-xs:px-5">
        <p className="text-xl tracking-[0.02em]">
          Manage how you pay for your plans in one place.
        </p>
        
        {/* Show Add Payment Method button if no payment methods exist */}
        {(!paymentMethods || paymentMethods.length === 0) && (
          <div className="flex justify-center mt-11">
            <button
              className="uppercase rounded-full bg-profile-header-bg hover:bg-black/45 py-3 px-8 text-white tracking-wider text-lg font-semimold"
              onClick={() => setIsEditOpen(true)}
            >
              Add Payment Method
            </button>
          </div>
        )}
        
        {paymentMethods?.map((paymentMethod: any) => (
          <div
            key={paymentMethod.id}
            className="flex justify-between mt-11 bg-white rounded-2xl py-4.5 px-7 max-l:flex-col"
          >
            <div className="flex flex-col">
              <p className="text-3xl font-bold tracking-wide text-profile-header-bg">
                Billing Account
              </p>
              <span className="mt-5 text-2xl max-sm:text-xl font-bold text-profile-header-bg max-xl:flex max-xl:flex-wrap">
                Payment Method:{' '}
                <span className="text-black !font-secondary">
                  {paymentMethod.type} - {paymentMethod.id}
                </span>{' '}
              </span>
              <span className="text-2xl max-sm:text-xl font-bold mt-2 text-profile-header-bg">
                {' '}
                is being used to pay for the following:
              </span>

              <ul className="list-disc ml-6.5 text-2xl max-sm:text-xl font-bold mt-2.5">
                {subscriptionPlans?.map((plan: any) => (
                  <li key={plan.id}>Plan ID: {plan.planId}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col justify-between mt-2 max-l:gap-8">
              <span className="text-xl !font-secondary py-2 px-4 tracking-tight bg-profile-badge-bg rounded-[10px] max-l:w-fit max-l:mt-2">
                Created: {new Date(paymentMethod.createdAt).toLocaleDateString()}
              </span>
              <button
                className="uppercase rounded-full bg-profile-header-bg hover:bg-black/45 w-[98%] py-3 text-white tracking-wider text-lg font-semimold"
                onClick={() => setIsEditOpen(true)}
              >
                Edit Payment Method
              </button>
            </div>
          </div>
        ))}
      </div>

      {isEditOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsEditOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[720px] mx-4 p-6">
            <button
              className="absolute right-4 top-3 text-2xl leading-none hover:text-black/70"
              aria-label="Close"
              onClick={() => setIsEditOpen(false)}
            >
              ×
            </button>
            <h3 className="text-xl font-bold mb-4 text-center">
              {(!paymentMethods || paymentMethods.length === 0) ? 'Add Payment Method' : 'Update Payment Method'}
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Securely connect your bank account to {(!paymentMethods || paymentMethods.length === 0) ? 'set up' : 'update'} your default payment method.
            </p>
            <StripeBanksElementForm setCurrentStep={() => {}} />
          </div>
        </div>
      )}
    </section>
  );
};
